<template>
  <div>
    <div class="page-header page-header-small">
      <parallax
        class="page-header-image"
        style="background-image: url('/img/tech.jpg')"
      >
      </parallax>
      <div class="content-center">
        <div class="container">
          <h1 class="title">Technical Projects</h1>
        </div>
      </div>
    </div>

    <div class="section tech-portfolio-section">
      <div class="container">
        <div class="tech-toolbar">
          <div class="tech-search">
            <i class="now-ui-icons ui-1_zoom-bold"></i>
            <input
              v-model.trim="searchQuery"
              type="text"
              class="form-control"
              placeholder="제목, 역할, 기술 스택, 설명으로 검색"
            />
          </div>
          <p class="tech-result-count">
            {{ filteredProjects.length }}개의 프로젝트
            <span v-if="searchQuery">· "{{ searchQuery }}" 검색 결과</span>
          </p>
        </div>

        <div class="tech-filters">
          <button
            type="button"
            class="tech-filter-btn"
            :class="{ active: selectedCategory === 'All' }"
            @click="setCategory('All')"
          >
            All
          </button>
          <button
            v-for="category in categories"
            :key="category"
            type="button"
            class="tech-filter-btn"
            :class="{ active: selectedCategory === category }"
            @click="setCategory(category)"
          >
            {{ category }}
          </button>
        </div>

        <div class="separator separator-primary"></div>

        <div class="row tech-grid-row">
          <div
            v-for="project in paginatedProjects"
            :key="project.slug"
            class="col-lg-4 col-md-6"
          >
            <router-link
              :to="{ name: 'tech-detail', params: { slug: project.slug } }"
              class="tech-card-link"
            >
              <article class="tech-card">
                <div
                  class="tech-card-cover"
                  :style="{ backgroundImage: `url('${project.coverImage}')` }"
                >
                  <div class="tech-card-overlay">
                    <span class="tech-card-category">{{ project.category }}</span>
                    <span class="tech-card-period">{{ project.period }}</span>
                  </div>
                </div>

                <div class="tech-card-body">
                  <p class="tech-card-role">{{ project.role }}</p>
                  <h3>{{ project.title }}</h3>
                  <p class="tech-card-summary">{{ project.summary }}</p>

                  <div class="tech-stack-list">
                    <span
                      v-for="stack in project.stack"
                      :key="`${project.slug}-${stack}`"
                      class="tech-stack-chip"
                    >
                      {{ stack }}
                    </span>
                  </div>
                </div>
              </article>
            </router-link>
          </div>
        </div>

        <div v-if="!paginatedProjects.length" class="tech-empty text-center">
          <h4>검색 결과가 없습니다.</h4>
          <p>다른 키워드나 카테고리로 다시 찾아보세요.</p>
        </div>

        <div
          v-if="pageCount > 1"
          class="pagination-container justify-content-center"
        >
          <n-pagination
            v-model="pagination.currentPage"
            type="info"
            :page-count="pageCount"
          >
            <template slot="prev">«</template>
            <template slot="next">»</template>
          </n-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Pagination, Parallax } from '@/components';
import projects from './data';

export default {
  name: 'tech-portfolio',
  bodyClass: 'landing-page',
  components: {
    [Pagination.name]: Pagination,
    Parallax
  },
  data() {
    return {
      allProjects: projects,
      searchQuery: '',
      selectedCategory: 'All',
      pagination: {
        currentPage: 1,
        perPage: 6
      }
    };
  },
  computed: {
    categories() {
      return [...new Set(this.allProjects.map(project => project.category))];
    },
    filteredProjects() {
      const keyword = this.searchQuery.toLowerCase();

      return this.allProjects.filter(project => {
        const matchesCategory =
          this.selectedCategory === 'All' ||
          project.category === this.selectedCategory;

        const matchesKeyword =
          !keyword ||
          [
            project.title,
            project.category,
            project.role,
            project.summary,
            ...(project.stack || [])
          ].some(value =>
            String(value)
              .toLowerCase()
              .includes(keyword)
          );

        return matchesCategory && matchesKeyword;
      });
    },
    pageCount() {
      return Math.max(
        1,
        Math.ceil(this.filteredProjects.length / this.pagination.perPage)
      );
    },
    paginatedProjects() {
      const start = (this.pagination.currentPage - 1) * this.pagination.perPage;
      const end = start + this.pagination.perPage;
      return this.filteredProjects.slice(start, end);
    }
  },
  methods: {
    setCategory(category) {
      this.selectedCategory = category;
    },
    scrollToPortfolioTop() {
      this.$nextTick(() => {
        const section = this.$el.querySelector('.tech-portfolio-section');
        if (!section) return;

        const offset = 40;
        const top =
          section.getBoundingClientRect().top + window.pageYOffset - offset;

        window.scrollTo({
          top,
          behavior: 'smooth'
        });
      });
    }
  },
  watch: {
    searchQuery() {
      this.pagination.currentPage = 1;
    },
    selectedCategory() {
      this.pagination.currentPage = 1;
    },
    filteredProjects() {
      if (this.pagination.currentPage > this.pageCount) {
        this.pagination.currentPage = this.pageCount;
      }
    },
    'pagination.currentPage'(newPage, oldPage) {
      if (newPage !== oldPage) {
        this.scrollToPortfolioTop();
      }
    }
  }
};
</script>

<style scoped>
.tech-hero-copy {
  max-width: 720px;
}

.tech-eyebrow {
  margin-bottom: 14px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.tech-hero-description {
  max-width: 620px;
  margin: 16px 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 1.05rem;
  line-height: 1.7;
}

.tech-portfolio-section {
  padding-bottom: 70px;
}

.tech-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 34px;
}

.tech-search {
  position: relative;
  flex: 1 1 420px;
  max-width: 560px;
}

.tech-search i {
  position: absolute;
  top: 50%;
  left: 18px;
  color: #adb5bd;
  transform: translateY(-50%);
}

.tech-search .form-control {
  height: 54px;
  padding-left: 48px;
  margin-bottom: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
}

.tech-result-count {
  margin-bottom: 0;
  color: #6c757d;
  font-weight: 600;
}

.tech-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 28px;
}

.tech-filter-btn {
  padding: 10px 18px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  background: #fff;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 600;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.tech-filter-btn:hover {
  border-color: rgba(44, 168, 255, 0.35);
  color: #2ca8ff;
}

.tech-filter-btn.active {
  border-color: #2ca8ff;
  background: #2ca8ff;
  color: #fff;
  box-shadow: 0 12px 24px rgba(44, 168, 255, 0.24);
}

.tech-grid-row > [class*='col-'] {
  margin-bottom: 32px;
}

.tech-card-link {
  display: block;
  height: 100%;
  color: inherit;
}

.tech-card-link:hover,
.tech-card-link:focus {
  color: inherit;
  text-decoration: none;
}

.tech-card {
  display: flex;
  height: 100%;
  overflow: hidden;
  flex-direction: column;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 45px rgba(44, 62, 80, 0.12);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.tech-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 54px rgba(44, 62, 80, 0.18);
}

.tech-card-cover {
  position: relative;
  min-height: 220px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.tech-card-cover::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.1) 0%,
    rgba(15, 23, 42, 0.8) 100%
  );
}

.tech-card-overlay {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px;
  color: #fff;
}

.tech-card-category,
.tech-card-period {
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.tech-card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px;
}

.tech-card-role {
  margin-bottom: 10px;
  color: #2ca8ff;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.tech-card-body h3 {
  margin-bottom: 14px;
  font-size: 1.35rem;
  line-height: 1.45;
}

.tech-card-summary {
  margin-bottom: 18px;
  color: #6c757d;
  line-height: 1.7;
}

.tech-stack-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 22px;
}

.tech-stack-chip {
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(44, 168, 255, 0.1);
  color: #1683d8;
  font-size: 0.78rem;
  font-weight: 700;
}

.tech-empty {
  padding: 30px 0 10px;
}

.tech-empty p {
  color: #6c757d;
}

:deep(.pagination-container) {
  margin-top: 26px;
}

@media (max-width: 991px) {
  .tech-card-cover {
    min-height: 200px;
  }
}

@media (max-width: 767px) {
  .tech-toolbar {
    margin-bottom: 28px;
  }

  .tech-grid-row > [class*='col-'] {
    margin-bottom: 24px;
  }

  .tech-card-overlay {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
